import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Mail,
  Phone,
  Building2,
  LogOut,
  Search,
  Inbox,
  Briefcase,
  Calendar,
  ExternalLink,
} from 'lucide-react';
import api, { formatApiErrorDetail } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { BRAND } from '@/data/content';

const TABS = [
  { key: 'contacts', label: 'Contact Enquiries', icon: Inbox },
  { key: 'careers', label: 'Career Applications', icon: Briefcase },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState('contacts');
  const [contacts, setContacts] = useState([]);
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [c1, c2] = await Promise.all([api.get('/contact'), api.get('/careers')]);
        if (cancelled) return;
        setContacts(c1.data || []);
        setCareers(c2.data || []);
      } catch (err) {
        if (!cancelled)
          setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredContacts = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) =>
      [c.name, c.email, c.company, c.subject, c.interest, c.message]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [contacts, query]);

  const filteredCareers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return careers;
    return careers.filter((c) =>
      [c.name, c.email, c.role, c.current_company, c.location, c.cover_letter]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [careers, query]);

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-[#0D1B2A] grain border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-11 h-11 bg-white rounded-sm grid place-items-center overflow-hidden">
              <img src={BRAND.logo} alt="Clan Group" className="w-9 h-9 object-contain" />
            </div>
            <div className="leading-tight">
              <div className="text-white font-display font-bold tracking-tight text-base">
                CLAN GROUP
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-semibold">
                Admin Console
              </div>
            </div>
          </Link>

          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-white/60 hover:text-[#D4AF37] transition-colors"
            >
              View Site <ExternalLink size={12} />
            </Link>
            <div className="hidden md:block text-right">
              <div className="text-white text-sm font-display font-semibold">
                {user?.name}
              </div>
              <div className="text-white/55 text-[11px] uppercase tracking-[0.22em]">
                {user?.email}
              </div>
            </div>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="border border-white/20 text-white px-4 py-2 text-xs uppercase tracking-[0.18em] font-bold hover:bg-white hover:text-[#0D1B2A] transition-colors inline-flex items-center gap-2"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 md:py-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="overline mb-3 flex items-center gap-3">
              <span className="gold-rule" /> Inbound
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0D1B2A] tracking-tight">
              Enquiries & Applications
            </h1>
          </div>
          <div className="flex items-stretch gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:w-80">
              <Search
                size={15}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#0D1B2A]/40"
              />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, email, subject..."
                data-testid="admin-search"
                className="w-full bg-white border border-[#0D1B2A]/12 pl-10 pr-4 py-3 text-[14px] text-[#0D1B2A] focus:border-[#D4AF37] outline-none"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-1 mb-8 border-b border-[#0D1B2A]/10">
          {TABS.map((t) => {
            const Icon = t.icon;
            const count = t.key === 'contacts' ? contacts.length : careers.length;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`admin-tab-${t.key}`}
                className={`flex items-center gap-2 px-5 py-3.5 text-[13px] font-bold uppercase tracking-[0.15em] transition-colors -mb-px border-b-2 ${
                  tab === t.key
                    ? 'text-[#0D1B2A] border-[#D4AF37]'
                    : 'text-[#4B5563] border-transparent hover:text-[#0D1B2A]'
                }`}
              >
                <Icon size={14} /> {t.label}
                <span
                  className={`ml-1 text-[10px] px-2 py-0.5 ${
                    tab === t.key
                      ? 'bg-[#D4AF37] text-[#0D1B2A]'
                      : 'bg-[#0D1B2A]/8 text-[#0D1B2A]'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {loading && (
          <div className="bg-white border border-[#0D1B2A]/8 p-10 text-center text-[#4B5563]">
            Loading...
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-100 text-red-700 p-5 mb-6">{error}</div>
        )}

        {!loading && !error && tab === 'contacts' && (
          <ContactsTable rows={filteredContacts} />
        )}
        {!loading && !error && tab === 'careers' && <CareersTable rows={filteredCareers} />}
      </main>
    </div>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function ContactsTable({ rows }) {
  if (rows.length === 0) {
    return (
      <div className="bg-white border border-[#0D1B2A]/8 p-10 text-center text-[#4B5563]">
        No contact enquiries yet.
      </div>
    );
  }
  return (
    <div className="grid gap-4" data-testid="admin-contacts-list">
      {rows.map((c) => (
        <article
          key={c.id}
          className="bg-white border border-[#0D1B2A]/10 p-6 md:p-7 grid md:grid-cols-12 gap-5"
        >
          <div className="md:col-span-4">
            <div className="font-display font-bold text-[#0D1B2A] text-lg">{c.name}</div>
            <a
              href={`mailto:${c.email}`}
              className="mt-1.5 text-sm text-[#0D1B2A]/80 hover:text-[#D4AF37] inline-flex items-center gap-2"
            >
              <Mail size={13} /> {c.email}
            </a>
            {c.phone && (
              <div className="mt-1 text-sm text-[#4B5563] inline-flex items-center gap-2">
                <Phone size={13} /> {c.phone}
              </div>
            )}
            {c.company && (
              <div className="mt-1 text-sm text-[#4B5563] inline-flex items-center gap-2">
                <Building2 size={13} /> {c.company}
              </div>
            )}
            <div className="mt-3 text-[11px] text-[#0D1B2A]/55 inline-flex items-center gap-1.5">
              <Calendar size={11} /> {formatDate(c.created_at)}
            </div>
          </div>
          <div className="md:col-span-8">
            <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold">
              {c.interest && (
                <span className="bg-[#0D1B2A] text-white px-2.5 py-1">{c.interest}</span>
              )}
              <span className="text-[#D4AF37]">{c.subject}</span>
            </div>
            <p className="mt-3 text-[#0D1B2A] text-[14px] leading-relaxed whitespace-pre-wrap">
              {c.message}
            </p>
          </div>
        </article>
      ))}
    </div>
  );
}

function CareersTable({ rows }) {
  if (rows.length === 0) {
    return (
      <div className="bg-white border border-[#0D1B2A]/8 p-10 text-center text-[#4B5563]">
        No applications yet.
      </div>
    );
  }
  return (
    <div className="grid gap-4" data-testid="admin-careers-list">
      {rows.map((c) => (
        <article
          key={c.id}
          className="bg-white border border-[#0D1B2A]/10 p-6 md:p-7 grid md:grid-cols-12 gap-5"
        >
          <div className="md:col-span-4">
            <div className="font-display font-bold text-[#0D1B2A] text-lg">{c.name}</div>
            <a
              href={`mailto:${c.email}`}
              className="mt-1.5 text-sm text-[#0D1B2A]/80 hover:text-[#D4AF37] inline-flex items-center gap-2"
            >
              <Mail size={13} /> {c.email}
            </a>
            {c.phone && (
              <div className="mt-1 text-sm text-[#4B5563] inline-flex items-center gap-2">
                <Phone size={13} /> {c.phone}
              </div>
            )}
            {c.location && (
              <div className="mt-1 text-sm text-[#4B5563]">{c.location}</div>
            )}
            <div className="mt-3 text-[11px] text-[#0D1B2A]/55 inline-flex items-center gap-1.5">
              <Calendar size={11} /> {formatDate(c.created_at)}
            </div>
          </div>
          <div className="md:col-span-8">
            <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold">
              <span className="bg-[#D4AF37] text-[#0D1B2A] px-2.5 py-1">{c.role}</span>
              {typeof c.experience_years === 'number' && (
                <span className="text-[#4B5563]">{c.experience_years} yrs</span>
              )}
              {c.current_company && (
                <span className="text-[#0D1B2A]/70">@ {c.current_company}</span>
              )}
            </div>
            {c.cover_letter && (
              <p className="mt-3 text-[#0D1B2A] text-[14px] leading-relaxed whitespace-pre-wrap">
                {c.cover_letter}
              </p>
            )}
            {c.portfolio_url && (
              <a
                href={c.portfolio_url}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#D4AF37] hover:underline"
              >
                Portfolio / LinkedIn <ExternalLink size={12} />
              </a>
            )}
          </div>
        </article>
      ))}
    </div>
  );
}
